import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Ban, Loader2, Mail, Send } from "lucide-react";

import { CustomDocName } from "@/components/application/CustomDocName";
import { staffFlowStepLabel } from "@/components/application/merchant-tasks/checklistLabels";
import CopyFlowLinkButton from "@/components/application/merchant-tasks/history/CopyFlowLinkButton";
import FlowDetailModal from "@/components/application/merchant-tasks/history/FlowDetailModal";
import { flowActionErrorMessage } from "@/components/application/merchant-tasks/history/flowActionError";
import {
  flowDetailQuery,
  liveFlowRefetch,
} from "@/components/application/merchant-tasks/history/queries";
import {
  FLOW_STATUS_META,
  STATUS_TONE_CLASSES,
  flowLabel,
} from "@/components/application/merchant-tasks/history/status";
import {
  invalidateConsequence,
  useFlowActions,
} from "@/components/application/merchant-tasks/history/useFlowActions";
import { FlowStepper, OnThePage } from "@/components/application/merchant-tasks/primitives";
import type { FlowDetail } from "@/components/application/merchant-tasks/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/utils/dateUtils";

import { THREAD_CARD } from "../choices/cardWidth";
import {
  AssistantCard,
  AssistantCardBody,
  AssistantCardFooter,
  AssistantCardHeader,
} from "../theme";
import { useLiveWhileVisible } from "./useLiveWhileVisible";

/** Narrower than a choice card (480px); edge to edge on a phone, like every thread card. */
const SENT_CARD = cn(THREAD_CARD, "max-w-[30rem]");

function Progress({ flow }: { flow: FlowDetail }) {
  if (flow.status !== "open") return null;
  if (flow.started_at == null && flow.last_activity_at == null) {
    // The page pings from the welcome screen on, before the merchant starts.
    return (
      <p className="text-muted-foreground">
        {flow.on_page ? "Viewing the invite" : "Not opened by the merchant yet"}
      </p>
    );
  }
  if (!flow.current_step) return null;
  return (
    <p className="text-muted-foreground">
      On{" "}
      <b className="font-medium text-foreground">
        {flow.current_step.custom ? (
          <CustomDocName name={flow.current_step.label} hint={false} />
        ) : (
          staffFlowStepLabel(flow.current_step)
        )}
      </b>
    </p>
  );
}

/**
 * Copy link, Resend invite and Invalidate…, as FlowDetailModal offers them; the tap is the action.
 * `outbound` false (the picked fake model) leaves Copy link only: resend emails the merchant and
 * invalidate voids the DocuSeal submissions.
 */
function Actions({
  borrowerId,
  flow,
  outbound,
}: {
  borrowerId: number;
  flow: FlowDetail;
  outbound: boolean;
}) {
  const actions = useFlowActions(borrowerId, flow.id);
  const { resend, invalidate, busy } = actions;
  const confirmingInvalidate = actions.confirmingInvalidate && outbound;
  if (!flow.link_active && !actions.failed) return null;
  return (
    // Above the stretched button, so these taps stay theirs.
    <div className="pointer-events-auto flex flex-col gap-2">
      {flow.link_active ? (
        <div
          data-testid="sent-flow-actions"
          className="flex flex-wrap items-center gap-2 [&_button]:h-7 [&_button]:gap-1 [&_button]:px-2 [&_button]:text-xs [&_svg]:size-3.5"
        >
          {confirmingInvalidate ? (
            <>
              <p className="w-full text-xs text-muted-foreground">{invalidateConsequence(flow)}</p>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={actions.invalidateLink}
              >
                {invalidate.isPending ? <Loader2 className="animate-spin" /> : <Ban />}
                Invalidate link
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => actions.setConfirmingInvalidate(false)}
              >
                Keep open
              </Button>
            </>
          ) : (
            <>
              <CopyFlowLinkButton borrowerId={borrowerId} flowId={flow.id} />
              {outbound && (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={actions.resendInvite}
                  >
                    {resend.isPending ? <Loader2 className="animate-spin" /> : <Mail />}
                    Resend invite
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="text-destructive hover:text-destructive"
                    disabled={busy}
                    onClick={() => actions.setConfirmingInvalidate(true)}
                  >
                    <Ban />
                    Invalidate…
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      ) : null}
      {resend.isSuccess ? (
        <p role="status" className="text-xs text-muted-foreground">
          Invite resent to {resend.data.recipient_email}.
        </p>
      ) : null}
      {actions.failed ? (
        <p role="alert" className="text-xs text-destructive">
          {flowActionErrorMessage(actions.failed)}
        </p>
      ) : null}
    </div>
  );
}

function SentFlow({
  borrowerId,
  flow,
  outbound,
}: {
  borrowerId: number;
  flow: FlowDetail;
  outbound: boolean;
}) {
  const [open, setOpen] = useState(false);
  const label = flowLabel(flow);
  const status = FLOW_STATUS_META[flow.status];
  return (
    <>
      {/* Stretched button: the card opens the flow's details; the actions sit above it. */}
      <button
        type="button"
        aria-label={`View ${label} flow`}
        className="absolute inset-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        onClick={() => setOpen(true)}
      />
      <div className="pointer-events-none relative">
        <AssistantCardHeader icon={Send} title={label}>
          {flow.on_page ? <OnThePage /> : null}
          <Badge variant="outline" className={STATUS_TONE_CLASSES[status.tone].badge}>
            {status.label}
          </Badge>
        </AssistantCardHeader>
        <AssistantCardBody className="flex gap-4 max-sm:flex-col max-sm:gap-3">
          <FlowStepper
            steps={flow.steps}
            currentKey={flow.status === "open" ? (flow.current_step?.key ?? null) : null}
            className="w-40 shrink-0 max-sm:w-auto"
          />
          <div className="min-w-0 flex-1 space-y-1 text-xs">
            <p className="text-muted-foreground">
              Sent <time dateTime={flow.created_at}>{formatDateTime(flow.created_at)}</time>
            </p>
            <Progress flow={flow} />
          </div>
        </AssistantCardBody>
        <AssistantCardFooter className="flex-col items-stretch gap-2 border-t pt-3">
          <p className="min-w-0 break-all text-xs text-muted-foreground">
            To {flow.recipient_email}
          </p>
          <Actions borrowerId={borrowerId} flow={flow} outbound={outbound} />
        </AssistantCardFooter>
      </div>
      <FlowDetailModal
        borrowerId={borrowerId}
        flowId={flow.id}
        open={open}
        onOpenChange={setOpen}
        outbound={outbound}
      />
    </>
  );
}

/**
 * A sent merchant task in the thread (F3): its steps, where the merchant is, whether their page is
 * open now, and the link's actions. While open, on screen and in a visible tab it re-reads the flow
 * every 5 s (S6, the cache entry FlowDetailModal and copy-link share). `outbound` false (on the
 * picked fake model) offers no Resend invite or Invalidate link, here or in its details.
 */
export function SentFlowCard({
  borrowerId,
  flowId,
  outbound = true,
}: {
  borrowerId: number;
  flowId: number;
  outbound?: boolean;
}) {
  const [ref, live] = useLiveWhileVisible<HTMLDivElement>();
  const detail = useQuery({
    ...flowDetailQuery(borrowerId, flowId),
    refetchInterval: (query) => (live ? liveFlowRefetch(query) : false),
  });
  const flow = detail.data;
  return (
    <AssistantCard
      ref={ref}
      data-merchant-task={flowId}
      data-live={live || undefined}
      className={cn(SENT_CARD, "relative transition-colors hover:border-foreground/25")}
    >
      {flow ? (
        <SentFlow borrowerId={borrowerId} flow={flow} outbound={outbound} />
      ) : (
        <>
          <AssistantCardHeader icon={Send} title="Merchant task" tone="muted" />
          <AssistantCardBody className="text-xs text-muted-foreground">
            {detail.isError ? (
              <span className="text-destructive">{flowActionErrorMessage(detail.error)}</span>
            ) : (
              "Loading…"
            )}
          </AssistantCardBody>
        </>
      )}
    </AssistantCard>
  );
}
